
import { useState, useEffect } from "react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { ClustererProvider } from "@/contexts/ClustererContext";
import { geocodeProperties, PropertyLocation } from "@/services/geocodingService";
import PropertyMapMarker from "./PropertyMapMarker";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Loader2 } from "lucide-react";

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  property_type: string;
  status: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface PropertyMapViewProps {
  properties: Property[];
  searchLocation?: string;
}

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '0.5rem'
};

const defaultCenter = {
  lat: 39.8283, // Center of US
  lng: -98.5795
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

const PropertyMapView = ({ properties, searchLocation }: PropertyMapViewProps) => {
  const [propertyLocations, setPropertyLocations] = useState<PropertyLocation[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Geocode properties when they change (with database/cache optimization)
  useEffect(() => {
    if (properties.length === 0) {
      setPropertyLocations([]);
      return;
    }

    const geocodeAllProperties = async () => {
      setIsGeocoding(true);
      try {
        // geocodeProperties now checks database coordinates first, then caches
        const locations = await geocodeProperties(properties);
        setPropertyLocations(locations);
      } catch (error) {
        console.error("Error geocoding properties:", error);
        setLoadError(true);
      } finally {
        setIsGeocoding(false);
      }
    };

    geocodeAllProperties();
  }, [properties]);

  if (!GOOGLE_MAPS_API_KEY || !GOOGLE_MAPS_MAP_ID) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-96 bg-muted rounded-lg">
            <div className="text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h3 className="text-lg font-semibold mb-2">Configuration Error</h3>
              <p className="text-muted-foreground">Google Maps configuration is missing.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-96 bg-muted rounded-lg">
            <div className="text-center">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Map</h3>
              <p className="text-muted-foreground">Unable to load Google Maps. Please try again later.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="relative">
          {isGeocoding && (
            <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading property locations...</span>
              </div>
            </div>
          )}
          
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <Map
              mapId={GOOGLE_MAPS_MAP_ID}
              style={mapContainerStyle}
              defaultCenter={defaultCenter}
              defaultZoom={4}
              gestureHandling="greedy"
              disableDefaultUI={false}
              zoomControl={true}
              fullscreenControl={true}
            >
              <ClustererProvider>
                {propertyLocations.map((location) => {
                  const property = properties.find(p => p.id === location.id);
                  if (!property) return null;
                  
                  return (
                    <PropertyMapMarker
                      key={location.id}
                      property={property}
                      position={{ lat: location.lat, lng: location.lng }}
                    />
                  );
                })}
              </ClustererProvider>
            </Map>
          </APIProvider>
          
          {propertyLocations.length === 0 && !isGeocoding && properties.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg">
              <div className="text-center bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Unable to locate properties on map
                </p>
              </div>
            </div>
          )}
        </div>
        
        {propertyLocations.length > 0 && (
          <div className="p-4 border-t bg-muted/50">
            <p className="text-sm text-muted-foreground text-center">
              Showing {propertyLocations.length} of {properties.length} properties on map
              {propertyLocations.length < properties.length && " (some locations could not be found)"}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyMapView;
